
import React, { useState, useEffect } from 'react';
import { type ErrorLog, logger } from '@/utils/logger';
import { toast } from 'sonner';
import ErrorLogHeader from './errors/ErrorLogHeader';
import ErrorLogTable from './errors/ErrorLogTable';
import EmptyErrorState from './errors/EmptyErrorState';
import { useErrorLogActions } from './errors/ErrorLogActions';

interface ErrorLogPanelProps {
  errors: ErrorLog[];
  onClearErrors: () => void;
}

const ErrorLogPanel: React.FC<ErrorLogPanelProps> = ({ errors, onClearErrors }) => {
  const [recentErrors, setRecentErrors] = useState<ErrorLog[]>(errors);
  
  // Update local state when props change
  useEffect(() => {
    setRecentErrors(errors);
  }, [errors]);
  
  // Set up real-time error logging
  useEffect(() => {
    // Polling for new errors every 10 seconds
    const intervalId = setInterval(() => {
      const currentErrors = logger.getRecentErrors();
      if (currentErrors.length !== recentErrors.length) {
        setRecentErrors(currentErrors);
        if (currentErrors.length > recentErrors.length) {
          const newErrorCount = currentErrors.length - recentErrors.length;
          toast.warning(`${newErrorCount} new error${newErrorCount > 1 ? 's' : ''} logged`);
        }
      }
    }, 10000);
    
    return () => clearInterval(intervalId);
  }, [recentErrors]);
  
  const { handleSendAllErrorReports } = useErrorLogActions(recentErrors);
  
  const handleRemoveError = (index: number) => {
    // Create a copy with the error removed
    const newErrors = [...recentErrors];
    newErrors.splice(index, 1);
    setRecentErrors(newErrors);
    
    // In a real implementation, you would call logger.removeError(errorId)
    toast.info('Error removed from the log');
  };

  return (
    <div className="space-y-4">
      <ErrorLogHeader 
        errorCount={recentErrors.length} 
        onSendAllReports={handleSendAllErrorReports}
        onClearErrors={onClearErrors}
      />
      
      {recentErrors.length === 0 ? (
        <EmptyErrorState />
      ) : (
        <ErrorLogTable 
          errors={recentErrors} 
          onRemoveError={handleRemoveError} 
        />
      )}
    </div>
  );
};

export default ErrorLogPanel;
